import { Box, Typography, Button, TextField, MenuItem, Chip, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import AddIcon from "@mui/icons-material/Add";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import useListDateFilter from "hooks/useListDateFilter";
import { useStockCountSessionList } from "api/hooks/inventory";

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Draft", value: "draft" },
  { label: "Completed", value: "completed" },
  { label: "Posted", value: "posted" },
];

const statusChip = (status) => {
  const map = {
    draft:     { label: "Draft",     color: "default" },
    completed: { label: "Completed", color: "warning" },
    posted:    { label: "Posted",    color: "success" },
  };
  const cfg = map[status] ?? { label: status, color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ fontSize: "11px", fontWeight: 600 }} />;
};

export default function StockTakeSessions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const {
    list,
    count,
    isLoadingList,
    isLoadingMore,
    hasMore,
    loadMore,
    dateFilter,
    setDateFilter,
    customRange,
    setCustomRange,
    applyCustomRange,
  } = useStockCountSessionList(business_branch_id, { statusFilter, search });

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const statusSelectSx = {
    minWidth: "160px",
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", borderRadius: "8px" },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.alt, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader icon={<FactCheckIcon sx={{ fontSize: "22px" }} />} title="Stock Take Sessions" count={count}>
        <Button
          onClick={() => navigate("/stockTakeManualForm")}
          startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}
        >
          Manual Count
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={statusSelectSx}
        >
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: "13px" }}>{o.label}</MenuItem>
          ))}
        </TextField>
        <ListSearchField placeholder="Search reference…" onSearch={setSearch} />
      </ListFilterBar>

      <ReportTable
        columns={[
          { label: "Reference" },
          { label: "Count Date" },
          { label: "Status" },
          { label: "Lines Total", align: "center" },
          { label: "Variances", align: "center" },
          { label: "Accurate", align: "center" },
          { label: "Journal", align: "center" },
          { label: "Actions", align: "center" },
        ]}
        rows={list}
        loading={isLoadingList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No stock take sessions found for this period"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{row.reference}</Typography></RCell>
            <RCell>{moment(row.count_date).format("LL")}</RCell>
            <RCell>{statusChip(row.status)}</RCell>
            <RCell align="center">{row.lines_total}</RCell>
            <RCell align="center">
              <Typography sx={{ fontSize: "13px", color: row.lines_with_variance > 0 ? "#c0392b" : "inherit" }}>
                {row.lines_with_variance}
              </Typography>
            </RCell>
            <RCell align="center">{row.lines_accurate}</RCell>
            <RCell align="center">
              {row.posted_journal_id ? (
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: accent }}>#{row.posted_journal_id}</Typography>
              ) : "—"}
            </RCell>
            <RCell align="center">
              <Box
                onClick={() => navigate("/stockTakeReview", { state: { sessionId: row.id } })}
                sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: `1px solid rgba(29,158,117,0.3)`, cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}
              >
                Review
              </Box>
            </RCell>
          </RRow>
        )}
      />
    </Box>
  );
}
